import { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDown, Globe, Heart, Zap, Eye, Sparkles, ArrowRight, Clock, Monitor, Cpu, Building2, Shield, TrendingUp, FileText, ExternalLink, BookOpen } from "lucide-react";
import axios from "axios";
import { usePrefetchedData } from "./common/DataPrefetch";
import { QRCodeCanvas } from "qrcode.react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ─── Animated Counter ─── */
const Counter = ({ end, duration = 2000, suffix = "" }) => {
  const hasValue = end != null && Number.isFinite(Number(end));
  const target = Math.max(0, hasValue ? Number(end) : 0);
  const isFloat = hasValue && !Number.isInteger(target);
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const visible = useRef(false);

  useEffect(() => {
    let raf;
    const animate = () => {
      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.max(0, eased * target));
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !visible.current) {
        visible.current = true;
        if (hasValue) animate();
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    // Re-animate when the real value arrives after the element is already visible
    // (previously a one-shot 'started' flag froze stale fallback numbers forever).
    if (visible.current && hasValue) animate();
    return () => { observer.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [target, duration, hasValue]);

  if (!hasValue) return <span ref={ref} className="tabular-nums opacity-40">—</span>;
  const shown = isFloat ? count.toFixed(1) : Math.floor(count).toLocaleString();
  return <span ref={ref} className="tabular-nums">{shown}{suffix}</span>;
};

/* ─── Section Reveal ─── */
const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.15 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(32px)", transition: `opacity 0.8s ease-out ${delay}s, transform 0.8s ease-out ${delay}s` }}>
      {children}
    </div>
  );
};

/* ─── Cosmic Background ─── */
const CosmicBackground = () => {
  const stars = useMemo(() => Array.from({ length: 140 }).map((_, i) => ({
    id: i,
    w: Math.random() * 2.5 + 0.5,
    x: Math.random() * 100,
    y: Math.random() * 100,
    o: Math.random() * 0.5 + 0.1,
    dur: 3 + Math.random() * 5,
    del: Math.random() * 6,
  })), []);

  const shootingStars = useMemo(() => Array.from({ length: 6 }).map((_, i) => ({
    id: i,
    x: 5 + Math.random() * 70,
    y: Math.random() * 80,
    del: 2 + i * 5 + Math.random() * 3,
    dur: 1.2 + Math.random() * 0.8,
    angle: 25 + Math.random() * 20,
    len: 80 + Math.random() * 60,
  })), []);

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="cosmic-bg">
      {/* Deep space base */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 15%, #0d1117 0%, #060a12 100%)" }} />

      {/* Milky Way band — luminous diagonal streak across full page */}
      <div className="absolute inset-0" style={{
        background: "linear-gradient(155deg, transparent 10%, rgba(139,92,246,0.06) 22%, rgba(99,102,241,0.14) 34%, rgba(167,139,250,0.18) 42%, rgba(200,180,255,0.20) 47%, rgba(167,139,250,0.18) 52%, rgba(99,102,241,0.14) 58%, rgba(139,92,246,0.06) 68%, transparent 82%)",
        animation: "milkyWayDrift 30s ease-in-out infinite alternate",
      }} />
      <div className="absolute inset-0" style={{
        background: "linear-gradient(155deg, transparent 18%, rgba(59,130,246,0.04) 30%, rgba(139,92,246,0.08) 40%, rgba(200,180,255,0.10) 47%, rgba(139,92,246,0.08) 54%, rgba(59,130,246,0.04) 62%, transparent 78%)",
        animation: "milkyWayDrift 25s ease-in-out infinite alternate-reverse",
      }} />
      {/* Milky Way warm underlayer */}
      <div className="absolute inset-0" style={{
        background: "linear-gradient(155deg, transparent 25%, rgba(245,158,11,0.03) 38%, rgba(236,72,153,0.04) 45%, rgba(245,158,11,0.04) 50%, rgba(236,72,153,0.03) 55%, transparent 70%)",
      }} />
      {/* Milky Way star dust grain — concentrated along the band */}
      <div className="absolute inset-0" style={{
        background: "linear-gradient(155deg, transparent 25%, rgba(255,255,255,0.03) 36%, rgba(255,255,255,0.06) 44%, rgba(255,255,255,0.07) 47%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.03) 58%, transparent 72%)",
      }} />

      {/* Nebula clouds — distributed across full page */}
      <div className="absolute" style={{
        width: 600, height: 600, top: "3%", left: "-5%",
        background: "radial-gradient(circle, rgba(139,92,246,0.12) 0%, rgba(139,92,246,0.04) 40%, transparent 70%)",
        borderRadius: "50%", filter: "blur(60px)",
        animation: "nebulaPulse 12s ease-in-out infinite alternate",
      }} />
      <div className="absolute" style={{
        width: 500, height: 500, top: "25%", right: "-8%",
        background: "radial-gradient(circle, rgba(16,185,129,0.10) 0%, rgba(16,185,129,0.03) 40%, transparent 70%)",
        borderRadius: "50%", filter: "blur(50px)",
        animation: "nebulaPulse 15s ease-in-out infinite alternate-reverse",
      }} />
      <div className="absolute" style={{
        width: 450, height: 450, top: "50%", left: "10%",
        background: "radial-gradient(circle, rgba(245,158,11,0.06) 0%, rgba(245,158,11,0.02) 40%, transparent 70%)",
        borderRadius: "50%", filter: "blur(40px)",
        animation: "nebulaPulse 10s ease-in-out infinite",
      }} />
      <div className="absolute" style={{
        width: 550, height: 550, top: "70%", right: "5%",
        background: "radial-gradient(circle, rgba(99,102,241,0.08) 0%, rgba(99,102,241,0.03) 40%, transparent 70%)",
        borderRadius: "50%", filter: "blur(55px)",
        animation: "nebulaPulse 14s ease-in-out 2s infinite alternate",
      }} />
      <div className="absolute" style={{
        width: 400, height: 400, top: "88%", left: "40%",
        background: "radial-gradient(circle, rgba(236,72,153,0.05) 0%, rgba(236,72,153,0.02) 40%, transparent 70%)",
        borderRadius: "50%", filter: "blur(45px)",
        animation: "nebulaPulse 11s ease-in-out 1s infinite alternate-reverse",
      }} />

      {/* Supernova core — pulsing glow in hero area */}
      <div className="absolute left-1/2 top-[15%]" style={{
        width: 300, height: 300, transform: "translate(-50%, -50%)",
        background: "radial-gradient(circle, rgba(139,92,246,0.15) 0%, rgba(99,102,241,0.08) 30%, rgba(16,185,129,0.04) 60%, transparent 80%)",
        borderRadius: "50%", filter: "blur(30px)",
        animation: "supernovaPulse 6s ease-in-out infinite",
      }} />
      <div className="absolute left-1/2 top-[15%]" style={{
        width: 120, height: 120, transform: "translate(-50%, -50%)",
        background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(139,92,246,0.06) 40%, transparent 70%)",
        borderRadius: "50%", filter: "blur(15px)",
        animation: "supernovaPulse 4s ease-in-out infinite alternate",
      }} />

      {/* Galaxy spiral arms — rotating */}
      <div className="absolute left-1/2 top-[15%]" style={{
        width: 700, height: 700, transform: "translate(-50%, -50%)",
        background: "conic-gradient(from 0deg, transparent 0%, rgba(139,92,246,0.04) 10%, transparent 20%, rgba(59,130,246,0.03) 30%, transparent 40%, rgba(16,185,129,0.03) 50%, transparent 60%, rgba(139,92,246,0.04) 70%, transparent 80%, rgba(245,158,11,0.02) 90%, transparent 100%)",
        borderRadius: "50%", filter: "blur(20px)",
        animation: "galaxySpin 60s linear infinite",
      }} />
      <div className="absolute left-1/2 top-[15%]" style={{
        width: 500, height: 500, transform: "translate(-50%, -50%)",
        background: "conic-gradient(from 180deg, transparent 0%, rgba(99,102,241,0.05) 15%, transparent 30%, rgba(16,185,129,0.04) 45%, transparent 60%, rgba(139,92,246,0.05) 75%, transparent 90%)",
        borderRadius: "50%", filter: "blur(15px)",
        animation: "galaxySpin 45s linear infinite reverse",
      }} />

      {/* Star field — full coverage */}
      {stars.map(s => (
        <div key={s.id} className="absolute rounded-full bg-white" style={{
          width: s.w, height: s.w,
          left: `${s.x}%`, top: `${s.y}%`,
          opacity: s.o,
          animation: `twinkle ${s.dur}s ease-in-out infinite`,
          animationDelay: `${s.del}s`,
        }} />
      ))}

      {/* Shooting stars */}
      {shootingStars.map(ss => (
        <div key={`ss-${ss.id}`} className="absolute" style={{
          left: `${ss.x}%`, top: `${ss.y}%`,
          width: ss.len, height: 1,
          background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.7) 60%, rgba(139,92,246,0.5) 100%)`,
          borderRadius: 1, filter: "blur(0.5px)",
          transform: `rotate(${ss.angle}deg)`,
          opacity: 0,
          animation: `shootingStar ${ss.dur}s ease-in ${ss.del}s infinite`,
        }} />
      ))}

      {/* Dust particles */}
      {Array.from({ length: 25 }).map((_, i) => (
        <div key={`dust-${i}`} className="absolute rounded-full" style={{
          width: 1, height: 1,
          left: `${5 + Math.random() * 90}%`,
          top: `${Math.random() * 100}%`,
          background: i % 3 === 0 ? "rgba(139,92,246,0.4)" : i % 3 === 1 ? "rgba(16,185,129,0.3)" : "rgba(245,158,11,0.3)",
          animation: `dustFloat ${8 + Math.random() * 12}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 10}s`,
        }} />
      ))}
    </div>
  );
};

/* ─── Main Landing Page ─── */
const LandingPage = ({ onEnter }) => {
  const prefetched = usePrefetchedData();
  const [pulse, setPulse] = useState(null);

  useEffect(() => {
    // Use the bootstrap-prefetched pulse if available; only fetch as a
    // fallback after a grace period if prefetch hasn't delivered.
    if (prefetched.pulse) { setPulse(prefetched.pulse); return; }
    const t = setTimeout(() => {
      if (!prefetched.pulse) {
        axios.get(`${API}/pulse`).then(r => setPulse(r.data)).catch(() => {});
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [prefetched.pulse]);

  const total = pulse?.total_entities ?? null;
  const countries = pulse?.countries_count ?? null;
  const carePct = pulse?.care_pct ?? null;
  const aicci = pulse?.aicci_global ?? null;
  const totalApps = pulse?.total_apps ?? null;
  const totalDevices = pulse?.total_devices ?? null;
  const totalCompanies = pulse?.total_companies ?? null;
  const totalPolicies = pulse?.total_policies ?? null;
  const totalPapers = pulse?.total_papers ?? null;
  const paperCountries = pulse?.papers_countries ?? null;
  const galaxies = prefetched.galaxyOverview?.galaxies || [];
  const carePctOf = (type, fallback) => {
    const g = galaxies.find(x => x.entity_type === type);
    return g?.care_pct != null ? g.care_pct : fallback;
  };
  const appsCarePct = carePctOf("application", null);
  const devicesCarePct = carePctOf("device", null);
  const companiesCarePct = carePctOf("company", null);
  const policyCarePct = carePctOf("policy", null);
  const orientPcts = (() => {
    const o = pulse?.orientations || {};
    const sum = (o.care || 0) + (o.utility || 0) + (o.creative || 0) + (o.extraction || 0);
    if (!sum) return { care: null, utility: null, creative: null, extraction: null, utilityNum: null, extractionCount: null };
    const p = k => `${((o[k] || 0) / sum * 100).toFixed(1)}%`;
    return {
      care: p("care"), utility: p("utility"), creative: p("creative"), extraction: p("extraction"),
      utilityNum: Number(((o.utility || 0) / sum * 100).toFixed(1)),
      extractionCount: o.extraction || null,
    };
  })();

  return (
    <div className="min-h-screen bg-[#060a12] text-slate-100 overflow-x-hidden relative" data-testid="landing-page">

      {/* Cosmic background — spans entire page */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <CosmicBackground />
      </div>

      {/* ═══ HERO ═══ */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 z-10" data-testid="hero-section">

        <div className="relative z-10 mx-auto space-y-8" style={{ maxWidth: "var(--content-max)" }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/20 bg-violet-500/[0.06]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[11px] text-violet-300 uppercase tracking-[0.25em] font-semibold">Live Observatory</span>
          </div>

          <h1 style={{ fontSize: "var(--text-hero)" }} className="font-black tracking-tight leading-[1.05]">
            <span className="bg-gradient-to-r from-slate-100 via-violet-200 to-slate-100 bg-clip-text text-transparent">
              The AI Orientation
            </span>
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-emerald-400 to-amber-400 bg-clip-text text-transparent">
              Observatory
            </span>
          </h1>

          <p style={{ fontSize: "var(--text-subtitle)" }} className="text-slate-400 mx-auto leading-relaxed" >
            Tracking the world's development in ethical AI.
            <br className="hidden sm:block" />
            <span className="text-white font-bold"><Counter end={total} /></span> AI entities
            across <span className="text-white font-bold"><Counter end={countries} /></span> countries.
            <span className="text-cyan-400 font-bold ml-1"><Counter end={totalPapers} /></span> research papers.
            <span className="text-emerald-400 font-bold ml-1"><Counter end={totalPolicies} /></span> policy frameworks.
          </p>

          <p className="text-sm text-amber-400/70 italic font-medium tracking-wide">
            Amplifying humanity. De-weaponizing progress.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <button onClick={onEnter} data-testid="enter-observatory-btn"
              className="group relative px-8 py-4 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold text-sm rounded-2xl transition-all shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 hover:-translate-y-0.5">
              Enter the Observatory
              <ArrowRight className="inline-block w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
            <a href="#why" className="text-sm text-slate-400 hover:text-slate-300 transition-colors flex items-center gap-1">
              Learn more <ChevronDown className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
          <span className="text-[10px] text-slate-400 uppercase tracking-widest">Scroll</span>
          <div className="w-5 h-8 rounded-full border border-slate-600 flex items-start justify-center p-1">
            <div className="w-1 h-2 rounded-full bg-slate-500 animate-bounce" />
          </div>
        </div>
      </section>

      {/* ═══ WHY THIS MATTERS ═══ */}
      <section id="why" className="relative z-10 py-24 px-6" data-testid="why-section">
        <div className="mx-auto" style={{ maxWidth: "var(--content-max)" }}>
          <Reveal>
            <div className="text-center mx-auto mb-16" style={{ maxWidth: "min(800px, 70vw)" }}>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-100 mb-6 leading-tight">
                We are building AI.
                <br />
                <span className="text-slate-400">But what are we becoming through it?</span>
              </h2>
              <p className="text-base text-slate-400 leading-relaxed">
                Every week, thousands of new AI products launch. They diagnose diseases, write code, 
                generate art, and surveil populations. Some are built to care. Others to extract. 
                Most go unexamined. This observatory exists because what we build reveals who we are. 
                And where we're headed.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Reveal delay={0.1}>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 text-center hover:border-blue-500/20 transition-all">
                <div className="text-3xl font-black text-blue-400 mb-2"><Counter end={orientPcts.utilityNum} suffix="%" /></div>
                <div className="text-xs text-slate-400 leading-relaxed">of AI is built for<br /><span className="text-blue-300 font-semibold">utility & efficiency</span></div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 text-center hover:border-emerald-500/20 transition-all">
                <div className="text-3xl font-black text-emerald-400 mb-2">Only <Counter end={carePct} suffix="%" /></div>
                <div className="text-xs text-slate-400 leading-relaxed">is built to<br /><span className="text-emerald-300 font-semibold">care for people</span></div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 text-center hover:border-red-500/20 transition-all">
                <div className="text-3xl font-black text-red-400 mb-2"><Counter end={orientPcts.extractionCount} /></div>
                <div className="text-xs text-slate-400 leading-relaxed">tools built for<br /><span className="text-red-300 font-semibold">extraction & surveillance</span></div>
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 text-center hover:border-violet-500/20 transition-all">
                <div className="text-3xl font-black text-violet-400 mb-2"><Counter end={aicci} suffix="/100" /></div>
                <div className="text-xs text-slate-400 leading-relaxed">global consciousness<br /><span className="text-violet-300 font-semibold">score (AICCI)</span></div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.5}>
            <p className="text-center text-sm text-slate-500 mt-8 italic max-w-xl mx-auto">
              The numbers don't lie. But they also don't speak for themselves. 
              That's why we built the observatory. To give the data a voice.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ THE FOUR ORIENTATIONS ═══ */}
      <section className="relative z-10 py-24 px-6" data-testid="orientations-section">
        <div className="mx-auto" style={{ maxWidth: "var(--content-max)" }}>
          <Reveal>
            <div className="text-center mb-14">
              <span className="text-[10px] text-violet-400 uppercase tracking-[0.3em] font-bold">How We See</span>
              <h2 className="text-3xl sm:text-4xl font-black mt-3">Everything we build with AI, carries an orientation founded deep in intent</h2>
              <p className="text-sm text-slate-400 mt-3 max-w-lg mx-auto">We classify every AI entity into four orientations, revealing what humanity prioritizes when it builds.</p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { name: "Care", color: "#10b981", icon: Heart, pct: orientPcts.care, desc: "Built to heal, support, and protect. Therapy bots, accessibility tools, elder care." },
              { name: "Utility", color: "#3b82f6", icon: Zap, pct: orientPcts.utility, desc: "Built for efficiency. Automation, analytics, productivity. The engine of industry." },
              { name: "Creative", color: "#8b5cf6", icon: Sparkles, pct: orientPcts.creative, desc: "Built to imagine. Art generators, music AI, storytelling. Humanity's mirror." },
              { name: "Extraction", color: "#ef4444", icon: Eye, pct: orientPcts.extraction, desc: "Built to extract. Surveillance, data harvesting, manipulation. The shadow side." },
            ].map((o, i) => (
              <Reveal key={o.name} delay={0.1 * i}>
                <div className="group bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 hover:border-opacity-30 transition-all text-center"
                  style={{ ["--hover-border"]: o.color }} onMouseEnter={e => e.currentTarget.style.borderColor = `${o.color}40`} onMouseLeave={e => e.currentTarget.style.borderColor = ""}>
                  <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${o.color}15` }}>
                    <o.icon className="w-5 h-5" style={{ color: o.color }} />
                  </div>
                  <div className="text-lg font-black mb-1" style={{ color: o.color }}>{o.name}</div>
                  <div className="text-2xl font-black text-slate-200 mb-3">{o.pct ?? "—"}</div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">{o.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.5}>
            <div className="mt-12 flex justify-center">
              <div className="bg-gradient-to-r from-violet-500/[0.06] to-emerald-500/[0.06] border border-white/[0.08] rounded-2xl px-8 py-6 text-center max-w-md">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-violet-400" />
                  <span className="text-[10px] text-violet-400 uppercase tracking-[0.2em] font-bold">Consciousness Score</span>
                </div>
                <div className="text-5xl font-black bg-gradient-to-r from-violet-400 to-emerald-400 bg-clip-text text-transparent">{aicci ?? "—"}</div>
                <div className="text-xs text-slate-400 mt-2">Global AICCI: measuring care, diversity, inclusion, and ethical restraint</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section className="relative z-10 py-24 px-6" data-testid="features-section">
        <div className="mx-auto" style={{ maxWidth: "var(--content-max)" }}>
          <Reveal>
            <div className="text-center mb-14">
              <span className="text-[10px] text-amber-400 uppercase tracking-[0.3em] font-bold">What You Can Do</span>
              <h2 className="text-3xl sm:text-4xl font-black mt-3">Tools to see what others miss</h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Reveal delay={0.1}>
              <div className="group bg-white/[0.02] border border-white/[0.06] rounded-2xl p-7 hover:border-emerald-500/20 transition-all cursor-pointer" onClick={onEnter}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="text-base font-bold text-slate-100">Compare</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mb-3">Put the US and Africa side by side. See who builds for care, who builds for extraction. And what the gap reveals about priorities.</p>
                <span className="text-xs text-emerald-400 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Try it <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="group bg-white/[0.02] border border-white/[0.06] rounded-2xl p-7 hover:border-amber-500/20 transition-all cursor-pointer" onClick={onEnter}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="text-base font-bold text-slate-100">Time Machine</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mb-3">Watch how AI consciousness evolved since 2020. See which countries are waking up. And which are doubling down on extraction.</p>
                <span className="text-xs text-amber-400 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Explore <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="group bg-white/[0.02] border border-white/[0.06] rounded-2xl p-7 hover:border-violet-500/20 transition-all cursor-pointer" onClick={onEnter}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-violet-400" />
                  </div>
                  <h3 className="text-base font-bold text-slate-100">Possibility Engine</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mb-3">Dream something. We'll map what already exists, what's emerging, and what the future holds. Then visualize the constellation.</p>
                <span className="text-xs text-violet-400 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Dream <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="group bg-white/[0.02] border border-white/[0.06] rounded-2xl p-7 hover:border-rose-500/20 transition-all cursor-pointer" onClick={onEnter}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-rose-400" />
                  </div>
                  <h3 className="text-base font-bold text-slate-100">The Mirror</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mb-3">What does AI tell us about ourselves? See humanity reflected in the technology it chooses to build. And the technology it doesn't.</p>
                <span className="text-xs text-rose-400 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Reflect <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ THE RESEARCH ═══ */}
      <section className="relative z-10 py-24 px-6" data-testid="research-section">
        <div className="mx-auto" style={{ maxWidth: "var(--content-max)" }}>
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl"
              style={{ background: "linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(16,185,129,0.04) 50%, rgba(139,92,246,0.06) 100%)", border: "1px solid rgba(99,102,241,0.15)" }}>
              <div className="p-8 md:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/15 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <span className="text-[10px] text-indigo-400 uppercase tracking-[0.2em] font-bold">Read the Research</span>
                    <p className="text-[10px] text-slate-500">Published on SSRN, 2026</p>
                  </div>
                </div>
                {/* Paper 1: CII */}
                <h3 className="text-lg md:text-xl font-black text-slate-100 leading-tight mb-2">
                  The Conscious Innovation Index (CII)
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">
                  A 7-Dimension Framework for Measuring the Ethical Alignment of Artificial Intelligence
                </p>
                {/* Paper 2: Possibility Engine */}
                <h3 className="text-lg md:text-xl font-black text-slate-100 leading-tight mb-2">
                  The Possibility Engine
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">
                  A Data-Grounded Framework for Software-Hardware AI Convergence and Future Capability Mapping
                </p>
                <p className="text-xs text-slate-400 mb-6">
                  By <span className="text-slate-300 font-medium">Priyanka Jha</span>
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {["AI Governance", "Responsible AI", "Innovation Policy", "Human-Centered AI", "Conscious Innovation", "AI Convergence"].map(tag => (
                    <span key={tag} className="text-[10px] px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-slate-400">{tag}</span>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <a href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=6492398" target="_blank" rel="noopener noreferrer" data-testid="ssrn-paper-link"
                    className="group flex items-center gap-2 px-5 py-2.5 bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/25 rounded-xl text-sm text-indigo-300 font-semibold transition-all">
                    CII Paper <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </a>
                  <a href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=6481799" target="_blank" rel="noopener noreferrer" data-testid="ssrn-pe-paper-link"
                    className="group flex items-center gap-2 px-5 py-2.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/25 rounded-xl text-sm text-emerald-300 font-semibold transition-all">
                    Possibility Engine Paper <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ THE GALAXIES ═══ */}
      <section className="relative z-10 py-24 px-6" data-testid="galaxies-section">
        <div className="mx-auto" style={{ maxWidth: "var(--content-max)" }}>
          <Reveal>
            <div className="text-center mb-14">
              <span className="text-[10px] text-emerald-400 uppercase tracking-[0.3em] font-bold">The Observatories</span>
              <h2 className="text-3xl sm:text-4xl font-black mt-3">Multiple lenses. One truth.</h2>
              <p className="text-sm text-slate-400 mt-3 max-w-lg mx-auto">Each galaxy observes a different dimension of AI's relationship with humanity.</p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            <Reveal delay={0.1}>
              <div className="group bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:border-blue-500/20 transition-all cursor-pointer" onClick={onEnter}>
                <Monitor className="w-6 h-6 text-blue-400 mb-4" />
                <h3 className="text-lg font-bold text-slate-100 mb-1">AI Apps</h3>
                <p className="text-xs text-slate-400 mb-4">Software applications: chatbots, generators, analyzers, creators</p>
                <div className="text-3xl font-black text-blue-400 mb-3"><Counter end={totalApps} /></div>
                <div className="w-full h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: "73%", background: "linear-gradient(90deg, #10b981, #3b82f6, #8b5cf6, #ef4444)" }} />
                </div>
                <div className="text-[10px] text-slate-400 mt-2">{appsCarePct != null ? `${appsCarePct}% care-oriented` : "care-oriented share loading"}</div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="group bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:border-emerald-500/20 transition-all cursor-pointer" onClick={onEnter}>
                <Cpu className="w-6 h-6 text-emerald-400 mb-4" />
                <h3 className="text-lg font-bold text-slate-100 mb-1">Physical AI</h3>
                <p className="text-xs text-slate-400 mb-4">Hardware: robots, wearables, embedded devices, drones</p>
                <div className="text-3xl font-black text-emerald-400 mb-3"><Counter end={totalDevices} /></div>
                <div className="w-full h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: "60%", background: "linear-gradient(90deg, #10b981, #3b82f6, #8b5cf6)" }} />
                </div>
                <div className="text-[10px] text-slate-400 mt-2">{devicesCarePct != null ? `${devicesCarePct}% care-oriented` : "care-oriented share loading"}</div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="group bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:border-amber-500/20 transition-all cursor-pointer" onClick={onEnter}>
                <Building2 className="w-6 h-6 text-amber-400 mb-4" />
                <h3 className="text-lg font-bold text-slate-100 mb-1">Companies</h3>
                <p className="text-xs text-slate-400 mb-4">Organizations building AI: who funds, who leads, who profits</p>
                <div className="text-3xl font-black text-amber-400 mb-3"><Counter end={totalCompanies} /></div>
                <div className="w-full h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: "55%", background: "linear-gradient(90deg, #10b981, #f59e0b, #ef4444)" }} />
                </div>
                <div className="text-[10px] text-slate-400 mt-2">{companiesCarePct != null ? `${companiesCarePct}% care-oriented` : "care-oriented share loading"}</div>
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="group bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:border-violet-500/20 transition-all cursor-pointer" onClick={onEnter}>
                <Shield className="w-6 h-6 text-violet-400 mb-4" />
                <h3 className="text-lg font-bold text-slate-100 mb-1">Policy</h3>
                <p className="text-xs text-slate-400 mb-4">AI regulations, governance frameworks, and ethical guidelines worldwide</p>
                <div className="text-3xl font-black text-violet-400 mb-3"><Counter end={totalPolicies} /></div>
                <div className="w-full h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: "65%", background: "linear-gradient(90deg, #10b981, #8b5cf6)" }} />
                </div>
                <div className="text-[10px] text-slate-400 mt-2">{policyCarePct != null ? `${policyCarePct}% care-oriented` : "care-oriented governance focus"}</div>
              </div>
            </Reveal>
            <Reveal delay={0.5}>
              <div className="group bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:border-cyan-500/20 transition-all cursor-pointer" onClick={onEnter}>
                <BookOpen className="w-6 h-6 text-cyan-400 mb-4" />
                <h3 className="text-lg font-bold text-slate-100 mb-1">Research</h3>
                <p className="text-xs text-slate-400 mb-4">Academic papers, breakthroughs, and the frontier of AI knowledge</p>
                <div className="text-3xl font-black text-cyan-400 mb-3"><Counter end={totalPapers} /></div>
                <div className="w-full h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pulse?.papers_care_pct || 30}%`, background: "linear-gradient(90deg, #10b981, #06b6d4)" }} />
                </div>
                <div className="text-[10px] text-slate-400 mt-2">{pulse?.papers_care_pct != null ? `${pulse.papers_care_pct}% care-oriented` : ""}{paperCountries != null ? ` · ${paperCountries} countries` : ""}</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ THE INVITATION ═══ */}
      <section className="relative z-10 py-32 px-6" data-testid="cta-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 50%, rgba(139,92,246,0.06) 0%, transparent 60%)" }} />
        </div>
        <div className="relative z-10 mx-auto text-center" style={{ maxWidth: "min(800px, 60vw)" }}>
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-6">
              The observatory is open.
              <br />
              <span className="bg-gradient-to-r from-violet-400 to-emerald-400 bg-clip-text text-transparent">What will you discover?</span>
            </h2>
            <p className="text-sm text-slate-400 mb-10 max-w-lg mx-auto leading-relaxed">
              Every data point is a choice someone made about what AI should do. 
              Together, they tell the story of what we value. And what we've forgotten to protect.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={onEnter} data-testid="cta-explore-btn"
                className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-emerald-600 hover:from-violet-500 hover:to-emerald-500 text-white font-bold text-sm rounded-2xl transition-all shadow-2xl shadow-violet-500/20 hover:shadow-violet-500/35 hover:-translate-y-0.5">
                Explore Now
                <ArrowRight className="inline-block w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={onEnter} data-testid="cta-contribute-btn"
                className="px-8 py-4 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] text-slate-300 font-semibold text-sm rounded-2xl transition-all">
                Contribute Data
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="relative z-10 border-t border-white/[0.04] py-12 px-6" data-testid="landing-footer">
        {/* Pitch card + QR */}
        <div className="max-w-5xl mx-auto mb-10 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6 flex flex-col sm:flex-row items-center gap-6" data-testid="landing-qr-section">
          <a href="/card.html" className="p-2.5 bg-white rounded-xl flex-shrink-0 hover:scale-105 transition-transform" data-testid="landing-qr-code" aria-label="Open the pitch card">
            <QRCodeCanvas value={`${window.location.origin}/card.html`} size={110} level="M" />
          </a>
          <div className="text-center sm:text-left">
            <h3 className="text-sm font-bold text-slate-200 mb-1">Take the Observatory with you</h3>
            <p className="text-xs text-slate-500 leading-relaxed mb-3 max-w-md">
              Scan to open the founder's pitch card — the project, the research papers, and the UN Digital Cooperation Portal enlisting — ready to share in the moment.
            </p>
            <a href="/card.html" data-testid="landing-view-card-link"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-400 hover:text-violet-300 transition-colors">
              View the card <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-violet-400" />
            <span className="text-sm font-bold text-slate-300">AIOO</span>
            <span className="text-xs text-slate-500">AI Orientation Observatory</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-slate-500">
            <button onClick={onEnter} className="hover:text-slate-300 transition-colors">Methodology</button>
            <button onClick={onEnter} className="hover:text-slate-300 transition-colors">About</button>
            <button onClick={onEnter} className="hover:text-slate-300 transition-colors">Contribute</button>
          </div>
          <div className="text-xs text-slate-700">
            &copy; {new Date().getFullYear()} Priyanka Jha. All rights reserved.
          </div>
        </div>
        <div className="text-center mt-6">
          <p className="text-[10px] text-slate-700 italic">Amplifying humanity. De-weaponizing progress.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
